import React, { useState } from 'react';

/**
 * AddTeacherHelp — single-page help guide for the Add Teacher dialog.
 * Self-contained: inline styles (no external CSS dependency).
 * Bilingual: English / Sinhala toggle with Abhaya Libre font for Sinhala.
 * Rendered as a full-screen modal overlay.
 */

// Bilingual text content
const t = {
    en: {
        headerTitle: '📚 Add New Teacher — Quick Guide',
        headerSub: 'Follow these 5 steps to add a teacher to the system',
        overview: '📋 Overview Flow',
        step1Title: 'Enrolment ID (emp_id)',
        step1Desc: 'Type the emp_id from the fingerprint machine',
        step1Body: 'Enter the emp_id from the fingerprint machine. This is the ID assigned when the teacher enrolled their fingerprint on the ZKTeco device. Validation runs automatically as you type.',
        step1MockLabel: 'Enrolment ID (emp_ID) *',
        step1MockValid: '✅ emp_id 385 is registered on the fingerprint machine (147 punches)',
        step1Callout: 'Auto-fill: If this emp_id is already linked to a profile in the database, the form fields below will be filled automatically.',
        step2Title: 'Sign ID (TID)',
        step2Desc: 'System suggests the next available Sign ID (TID)',
        step2Body: 'The system suggests the next available Sign ID (TID). This is the school\'s internal sequential number for the teacher. Edit if needed.',
        step2MockLabel: 'Sign ID (TID) *',
        step2MockHint: 'Suggested next ID — editable if needed',
        step2Callout: '⚠ Important: TID and emp_id are different numbers. TID is assigned by the school. emp_id is assigned by the fingerprint machine.',
        step3Title: 'NIC Number',
        step3Desc: 'Validate format → search DB → auto-create draft + start backfill',
        step3Body: 'Enter the teacher\'s NIC number. The system validates the format first, then searches the database for an existing profile.',
        step3FormatTitle: 'NIC Format Validation:',
        step3FormatOld: 'Old NIC: 9 digits + V (e.g., 883382951V)',
        step3FormatNew: 'New NIC: 12 digits (e.g., 198955200637)',
        step3FormatInvalid: 'Invalid format shows a red error message.',
        step3MockLabel: 'NIC Number *',
        step3MockValid: '✅ Valid NIC format',
        step3CalloutExisting: 'Existing profile: If the NIC is found at another school, the profile will be linked to Ananda College when you save. No need to re-enter details.',
        step3CalloutNew: 'New teacher: If the NIC is not found AND emp_id is validated, the system silently creates a draft record (emp_id + TID + NIC) and immediately starts backfilling attendance from WDMS. A progress bar appears below the NIC field.',
        step3BackfillTitle: 'Backfill Progress (runs in background):',
        step3BackfillMock: '🔄 Backfilling attendance from WDMS... [████████░░░░] 45/148',
        step3BackfillNote: 'You can continue entering personal details while backfill runs. No need to wait.',
        step4Title: 'Personal Details',
        step4Desc: 'Fill in name, type, email, WhatsApp number',
        step4Body: 'Fill in the following fields while the backfill runs in the background:',
        step4F1: 'First Name — Teacher\'s first name',
        step4F2: 'Last Name — Teacher\'s last name',
        step4F3: 'Teacher Type — Select from dropdown (6-11, A/L, Primary, etc.)',
        step4F4: 'Email — Email address',
        step4F5: 'WhatsApp No — WhatsApp contact number',
        step5Title: 'Save & Print',
        step5Desc: 'Click Save to complete the teacher record. Print the registration slip.',
        step5Body: 'Click the Save button. The system updates the draft record with the full details you entered. On success, a green confirmation appears with the teacher\'s Emp ID and Sign ID. You can then Print Registration slip or close the dialog.',
        step5MockSaved: '✅ Teacher saved!  Emp ID: 385 | Sign ID: 503',
        step5MockPrint: '🖨 Print Registration',
        notesTitle: '⚠ Key Things to Remember',
        note1: 'emp_id ≠ TID: The Enrolment ID (emp_id) comes from the fingerprint machine. The Sign ID (TID) is the school\'s sequential number. They are different systems.',
        note2: 'Validation: The emp_id must have punch records on the fingerprint machine. If validation fails (red message), the emp_id is not enrolled on any machine.',
        note3: 'NIC Format: Old NIC = 9 digits + V. New NIC = 12 digits. Invalid format blocks lookup and draft creation.',
        note4: 'Auto-Draft + Backfill: When NIC is new and emp_id is validated, a draft is created silently and backfill starts immediately. You can enter details while backfill runs.',
        note5: 'Drafts Persist: If you close without saving, the draft remains. Next time you enter the same emp_id + TID + NIC, the system recognizes the draft and lets you complete the details.',
        footer: 'Need more help? Contact the system administrator.',
        badgeRequired: 'Required',
        badgeAuto: 'Auto-suggested',
        badgeFinal: 'Final',
        badgeNew: 'New'
    },
    si: {
        headerTitle: '📚 නව ගුරුවරයෙකු එක් කිරීම — ක්ෂිප්‍ර මාර්ගෝපදේශය',
        headerSub: 'ගුරුවරයෙකු පද්ධතියට එක් කිරීමට මෙම පියවර 5 ක් අනුගමනය කරන්න',
        overview: '📋 සාරාංශ ප්‍රවාහය',
        step1Title: 'ලියාපදිංචි අංකය (emp_id)',
        step1Desc: 'ඇඟිලි සලකුණු යන්ත්‍රයෙන් emp_id එක ටයිප් කරන්න',
        step1Body: 'ඇඟිලි සලකුණු යන්ත්‍රයෙන් (ZKTeco) emp_id එක ඇතුළත් කරන්න. මෙය ගුරුවරයාගේ ඇඟිලි සලකුණ ලියාපදිංචි කිරීමේදී යන්ත්‍රය විසින් ලබා දුන් අංකයයි. ටයිප් කිරීමේදී ස්වයංක්‍රීයව තහවුරු කිරීම සිදු වේ.',
        step1MockLabel: 'ලියාපදිංචි අංකය (emp_ID) *',
        step1MockValid: '✅ emp_id 385 ඇඟිලි සලකුණු යන්ත්‍රයේ ලියාපදිංචි වී ඇත (147 punches)',
        step1Callout: 'ස්වයංක්‍රීය පිරවීම: මෙම emp_id එක දැනටමත් database එකේ profile එකකට සම්බන්ධ වී ඇත්නම්, පහත විස්තර ක්ෂේත්‍ර ස්වයංක්‍රීයව පිරවේ.',
        step2Title: 'සංකේත අංකය (TID)',
        step2Desc: 'පද්ධතිය ඊළඟ සංකේත අංකය (TID) යෝජනා කරයි',
        step2Body: 'පද්ධතිය ඊළඟ ලබා දුන් TID අංකය යෝජනා කරයි. මෙය පාසලේ ගුරුවරුන් සඳහා අනුප්‍රාදේශීය අංකයයි. අවශ්‍ය නම් වෙනස් කරන්න.',
        step2MockLabel: 'සංකේත අංකය (TID) *',
        step2MockHint: 'යෝජිත ඊළඟ අංකය — අවශ්‍ය නම් වෙනස් කරන්න',
        step2Callout: '⚠ වැදගත්: TID සහ emp_id වෙනස් අංක වේ. TID පාසල විසින් ලබා දේ. emp_id ඇඟිලි සලකුණු යන්ත්‍රය විසින් ලබා දේ.',
        step3Title: 'ජාතික හැඳුනුම්පත් අංකය (NIC)',
        step3Desc: 'ආකෘතිය තහවුරු කරයි → DB සොයයි → ස්වයංක්‍රීය draft + backfill',
        step3Body: 'ගුරුවරයාගේ NIC අංකය ඇතුළත් කරන්න. පද්ධතිය පළමුව ආකෘතිය තහවුරු කරයි, ඉන්පසු database එකේ පවතින profile එකක් සොයයි.',
        step3FormatTitle: 'NIC ආකෘති තහවුරු කිරීම:',
        step3FormatOld: 'පැරණි NIC: ඉලක්කම් 9 + V (උදා: 883382951V)',
        step3FormatNew: 'නව NIC: ඉලක්කම් 12 (උදා: 198955200637)',
        step3FormatInvalid: 'වැරදි ආකෘතියකින් යුක්ත නම් රතු පැහැ දෝෂ පණිවිඩයක් දිස් වේ.',
        step3MockLabel: 'NIC අංකය *',
        step3MockValid: '✅ වලංගු NIC ආකෘතිය',
        step3CalloutExisting: 'පවතින profile: වෙනත් පාසලක NIC හමු වුවහොත්, profile එක සුරැකීමේදී ආනන්ද විද්‍යාලයට සම්බන්ධ වේ. විස්තර නැවත ඇතුළත් කිරීමට අවශ්‍ය නැත.',
        step3CalloutNew: 'නව ගුරුවරයා: NIC හමු නොවී එයින් emp_id තහවුරු වී ඇත්නම්, පද්ධතිය නිහඬව draft record එකක් (emp_id + TID + NIC) සාදා, ඉක්මනින් WDMS යෙන් attendance backfill කිරීම ආරම්භ කරයි. NIC ක්ෂේත්‍රය යටතේ progress bar එකක් දිස් වේ.',
        step3BackfillTitle: 'Backfill ප්‍රගතිය (පසුබිමේ වැඩ කරයි):',
        step3BackfillMock: '🔄 WDMS යෙන් attendance backfill කරයි... [████████░░░░] 45/148',
        step3BackfillNote: 'Backfill වැඩ කරන අතරතුර පෞද්ගලික විස්තර ඇතුළත් කිරීමට පුළුවන්. බලා සිටීමට අවශ්‍ය නැත.',
        step4Title: 'පෞද්ගලික විස්තර',
        step4Desc: 'නම, වර්ගය, email, WhatsApp අංකය පුරන්න',
        step4Body: 'Backfill පසුබිමේ දී වැඩ කරන අතරතුර පහත ක්ෂේත්‍ර පුරන්න:',
        step4F1: 'මුල් නම — ගුරුවරයාගේ මුල් නම',
        step4F2: 'අග නම — ගුරුවරයාගේ අග නම',
        step4F3: 'ගුරු වර්ගය — Dropdown එකෙන් තෝරන්න (6-11, A/L, Primary ආදිය)',
        step4F4: 'Email — ඊමේල් ලිපිනය',
        step4F5: 'WhatsApp අංකය — WhatsApp සම්බන්ධතා අංකය',
        step5Title: 'සුරකින්න සහ මුද්‍රණය කරන්න',
        step5Desc: 'ගුරුවරයාගේ record එක සම්පූර්ණ කිරීමට Save ක්ලික් කරන්න. ලියාපදිංචි පත්‍රය මුද්‍රණය කරන්න.',
        step5Body: 'Save බොත්තම ක්ලික් කරන්න. පද්ධතිය ඔබ ඇතුළත් කළ සම්පූර්ණ විස්තර සමග draft record එක update කරයි. සාර්ථක වූ විට, ගුරුවරයාගේ Emp ID සහ Sign ID සහිත කොළ පැහැයේ තහවුරු කිරීමක් දිස් වේ. ඉන්පසු ලියාපදිංචි පත්‍රය මුද්‍රණය කරන්න හෝ වසා දැමිය හැක.',
        step5MockSaved: '✅ ගුරුවරයා සුරැකිණි!  Emp ID: 385 | Sign ID: 503',
        step5MockPrint: '🖨 ලියාපදිංචි පත්‍රය මුද්‍රණය කරන්න',
        notesTitle: '⚠ මතක තබා ගත යුතු ප්‍රධාන කරුණු',
        note1: 'emp_id ≠ TID: ලියාපදිංචි අංකය (emp_id) ඇඟිලි සලකුණු යන්ත්‍රයෙන් ලැබේ. සංකේත අංකය (TID) පාසලේ අනුප්‍රාදේශීය අංකයයි. මෙය වෙනස් පද්ධති දෙකකි.',
        note2: 'තහවුරු කිරීම: emp_id එකට ඇඟිලි සලකුණු යන්ත්‍රයේ punch records තිබිය යුතුය. තහවුරු කිරීම අසාර්ථක වුවහොත් (රතු පණිවිඩය), emp_id එක කිසිදු යන්ත්‍රයක ලියාපදිංචි වී නැත.',
        note3: 'NIC ආකෘතිය: පැරණි NIC = ඉලක්කම් 9 + V. නව NIC = ඉලක්කම් 12. වැරදි ආකෘතියකින් lookup සහ draft සෑදීම නවතී.',
        note4: 'ස්වයං-Draft + Backfill: NIC එක නව වී emp_id තහවුරු වී ඇත්නම්, නිහඬව draft එකක් සාදා backfill ඉක්මනින් ආරම්භ වේ. විස්තර ඇතුළත් කරන අතරතුර backfill වැඩ කරයි.',
        note5: 'Drafts රඳවා තබයි: සුරැකීමකින් තොරව වැසුවත්, draft එක රිය දිව්. ඊළඟ වරට එම emp_id + TID + NIC ඇතුළත් කළ විට, පද්ධතිය draft එක හඳුනාගෙන විස්තර සම්පූර්ණ කිරීමට ඉඩ දෙයි.',
        footer: 'තවත් උදව් අවශ්‍යද? පද්ධති පරිපාලකයා අමතන්න.',
        badgeRequired: 'අත්‍යවශ්‍ය',
        badgeAuto: 'ස්වයං-යෝජිත',
        badgeFinal: 'අවසාන',
        badgeNew: 'නව'
    }
};

export default function AddTeacherHelp({ onClose }) {
    const [lang, setLang] = useState('en');
    const isSi = lang === 'si';
    const s = t[lang];

    // Abhaya Libre font for Sinhala
    const fontFamily = isSi
        ? "'Abhaya Libre', 'Noto Sans Sinhala', sans-serif"
        : "system-ui, -apple-system, sans-serif";

    const styles = {
        overlay: {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
            overflowY: 'auto', padding: '20px'
        },
        card: {
            backgroundColor: '#f8fafc', maxWidth: '720px', width: '100%',
            borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            margin: '20px 0', fontFamily
        },
        header: {
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #10b981 100%)',
            padding: '28px 32px', color: 'white', position: 'relative'
        },
        headerTitle: { fontSize: isSi ? '1.45rem' : '1.6rem', fontWeight: 700, margin: 0, fontFamily },
        headerSub: { fontSize: isSi ? '0.95rem' : '0.95rem', opacity: 0.9, marginTop: '6px', fontFamily },
        closeBtn: {
            position: 'absolute', top: '20px', right: '24px',
            backgroundColor: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
            width: '40px', height: '40px', color: 'white', fontSize: '1.3rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s'
        },
        langToggle: {
            position: 'absolute', top: '22px', right: '76px',
            display: 'flex', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '20px',
            overflow: 'hidden', border: 'none'
        },
        langBtn: {
            padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
            border: 'none', color: 'white', backgroundColor: 'transparent', transition: 'background 0.2s', fontFamily
        },
        langBtnActive: {
            backgroundColor: 'white', color: '#1e40af'
        },
        body: { padding: '28px 32px', fontFamily },
        // Flow chart
        flowContainer: {
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, marginBottom: '32px'
        },
        flowStep: {
            display: 'flex', alignItems: 'center', gap: '12px',
            backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '12px',
            padding: '14px 20px', width: '100%', maxWidth: '480px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        },
        flowNum: {
            flexShrink: 0, width: '36px', height: '36px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, color: 'white', fontSize: '1rem'
        },
        flowArrow: {
            width: '2px', height: '24px', backgroundColor: '#cbd5e1'
        },
        // Step sections
        section: {
            backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px',
            marginBottom: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
        },
        sectionTitle: { fontSize: '1.15rem', fontWeight: 700, margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: '8px', fontFamily },
        sectionText: { fontSize: '0.92rem', color: '#374151', lineHeight: 1.6, margin: 0, fontFamily },
        // Mock field
        mockField: {
            backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '12px 16px',
            marginTop: '12px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: '0.85rem'
        },
        mockLabel: { fontSize: '0.75rem', color: '#64748b', marginBottom: '4px', fontWeight: 600, fontFamily },
        mockInput: {
            backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px',
            padding: '8px 12px', fontSize: '1rem', fontWeight: 700, textAlign: 'center', color: '#1e293b'
        },
        // Callouts
        callout: {
            borderRadius: '10px', padding: '14px 18px', marginBottom: '12px', fontSize: '0.88rem', lineHeight: 1.5, fontFamily
        },
        calloutBlue: { backgroundColor: '#eff6ff', borderLeft: '4px solid #3b82f6', color: '#1e40af' },
        calloutGreen: { backgroundColor: '#ecfdf5', borderLeft: '4px solid #10b981', color: '#065f46' },
        calloutAmber: { backgroundColor: '#fffbeb', borderLeft: '4px solid #f59e0b', color: '#92400e' },
        calloutRed: { backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', color: '#991b1b' },
        // Badge
        badge: {
            display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
            fontSize: '0.72rem', fontWeight: 600, marginLeft: '8px', fontFamily
        },
        footer: {
            textAlign: 'center', padding: '20px', color: '#64748b', fontSize: '0.85rem',
            borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', fontFamily
        }
    };

    const steps = [
        { num: 1, color: '#3b82f6', label: s.step1Title, desc: s.step1Desc },
        { num: 2, color: '#8b5cf6', label: s.step2Title, desc: s.step2Desc },
        { num: 3, color: '#ec4899', label: s.step3Title, desc: s.step3Desc },
        { num: 4, color: '#f59e0b', label: s.step4Title, desc: s.step4Desc },
        { num: 5, color: '#10b981', label: s.step5Title, desc: s.step5Desc }
    ];

    return (
        <>
            {/* Inject Abhaya Libre font from Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Abhaya+Libre:wght@400;500;600;700&display=swap');
            `}</style>
            <div style={styles.overlay} onClick={onClose}>
                <div style={styles.card} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div style={styles.header}>
                        <button style={styles.closeBtn} onClick={onClose} aria-label="Close help">✕</button>
                        {/* Language Toggle */}
                        <div style={styles.langToggle}>
                            <button
                                style={{ ...styles.langBtn, ...(lang === 'en' ? styles.langBtnActive : {}) }}
                                onClick={() => setLang('en')}
                            >EN</button>
                            <button
                                style={{ ...styles.langBtn, ...(lang === 'si' ? styles.langBtnActive : {}) }}
                                onClick={() => setLang('si')}
                            >සිං</button>
                        </div>
                        <h2 style={styles.headerTitle}>{s.headerTitle}</h2>
                        <p style={styles.headerSub}>{s.headerSub}</p>
                    </div>

                    <div style={styles.body}>
                        {/* Flow Chart */}
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', color: '#1e293b', fontFamily }}>
                            {s.overview}
                        </h3>
                        <div style={styles.flowContainer}>
                            {steps.map((step, i) => (
                                <React.Fragment key={step.num}>
                                    <div style={styles.flowStep}>
                                        <div style={{ ...styles.flowNum, backgroundColor: step.color }}>{step.num}</div>
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', fontFamily }}>{step.label}</div>
                                            <div style={{ fontSize: '0.82rem', color: '#64748b', fontFamily }}>{step.desc}</div>
                                        </div>
                                    </div>
                                    {i < steps.length - 1 && <div style={styles.flowArrow} />}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Step 1 */}
                        <div style={styles.section}>
                            <h4 style={{ ...styles.sectionTitle, color: '#1e40af' }}>
                                <span style={{ ...styles.flowNum, backgroundColor: '#3b82f6', width: '28px', height: '28px', fontSize: '0.85rem' }}>1</span>
                                {s.step1Title}
                                <span style={{ ...styles.badge, backgroundColor: '#dbeafe', color: '#1e40af' }}>{s.badgeRequired}</span>
                            </h4>
                            <p style={styles.sectionText}>{s.step1Body}</p>
                            <div style={styles.mockField}>
                                <div style={styles.mockLabel}>{s.step1MockLabel}</div>
                                <div style={styles.mockInput}>385</div>
                                <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#10b981', fontFamily }}>
                                    {s.step1MockValid}
                                </div>
                            </div>
                            <div style={{ ...styles.callout, ...styles.calloutGreen, marginTop: '12px' }}>
                                {s.step1Callout}
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div style={styles.section}>
                            <h4 style={{ ...styles.sectionTitle, color: '#6d28d9' }}>
                                <span style={{ ...styles.flowNum, backgroundColor: '#8b5cf6', width: '28px', height: '28px', fontSize: '0.85rem' }}>2</span>
                                {s.step2Title}
                                <span style={{ ...styles.badge, backgroundColor: '#ede9fe', color: '#6d28d9' }}>{s.badgeAuto}</span>
                            </h4>
                            <p style={styles.sectionText}>{s.step2Body}</p>
                            <div style={styles.mockField}>
                                <div style={styles.mockLabel}>{s.step2MockLabel}</div>
                                <div style={styles.mockInput}>503</div>
                                <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#64748b', fontFamily }}>
                                    {s.step2MockHint}
                                </div>
                            </div>
                            <div style={{ ...styles.callout, ...styles.calloutRed, marginTop: '12px' }}>
                                {s.step2Callout}
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div style={styles.section}>
                            <h4 style={{ ...styles.sectionTitle, color: '#be185d' }}>
                                <span style={{ ...styles.flowNum, backgroundColor: '#ec4899', width: '28px', height: '28px', fontSize: '0.85rem' }}>3</span>
                                {s.step3Title}
                                <span style={{ ...styles.badge, backgroundColor: '#fce7f3', color: '#be185d' }}>{s.badgeRequired}</span>
                            </h4>
                            <p style={styles.sectionText}>{s.step3Body}</p>

                            {/* NIC Format Validation */}
                            <div style={{ ...styles.callout, ...styles.calloutAmber, marginTop: '12px' }}>
                                <strong>{s.step3FormatTitle}</strong>
                                <ul style={{ margin: '6px 0 0 0', paddingLeft: '18px', fontSize: '0.85rem' }}>
                                    <li>{s.step3FormatOld}</li>
                                    <li>{s.step3FormatNew}</li>
                                </ul>
                                <div style={{ marginTop: '4px', fontSize: '0.82rem' }}>{s.step3FormatInvalid}</div>
                            </div>

                            <div style={styles.mockField}>
                                <div style={styles.mockLabel}>{s.step3MockLabel}</div>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <div style={{ ...styles.mockInput, flex: 1, textAlign: 'left', fontWeight: 400 }}>198955200637</div>
                                    <div style={{ ...styles.mockInput, backgroundColor: '#3b82f6', color: 'white', width: '36px', fontWeight: 400 }}>🔍</div>
                                </div>
                                <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#10b981', fontFamily }}>
                                    {s.step3MockValid}
                                </div>
                            </div>

                            {/* Backfill progress mock */}
                            <div style={{
                                marginTop: '12px', padding: '12px',
                                backgroundColor: '#fff3e0', borderRadius: '8px',
                                border: '1px solid #ff9800'
                            }}>
                                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#92400e', fontFamily, marginBottom: '6px' }}>
                                    {s.step3BackfillTitle}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#1f2937', fontFamily, fontFamily }}>
                                    {s.step3BackfillMock}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', fontFamily, marginTop: '4px' }}>
                                    {s.step3BackfillNote}
                                </div>
                            </div>

                            <div style={{ ...styles.callout, ...styles.calloutBlue, marginTop: '12px' }}>
                                {s.step3CalloutExisting}
                            </div>
                            <div style={{ ...styles.callout, ...styles.calloutGreen }}>
                                {s.step3CalloutNew}
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div style={styles.section}>
                            <h4 style={{ ...styles.sectionTitle, color: '#b45309' }}>
                                <span style={{ ...styles.flowNum, backgroundColor: '#f59e0b', width: '28px', height: '28px', fontSize: '0.85rem' }}>4</span>
                                {s.step4Title}
                                <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#b45309' }}>{s.badgeRequired}</span>
                            </h4>
                            <p style={styles.sectionText}>{s.step4Body}</p>
                            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: '0.88rem', color: '#374151', lineHeight: 1.8, fontFamily }}>
                                <li><strong>{s.step4F1}</strong></li>
                                <li><strong>{s.step4F2}</strong></li>
                                <li><strong>{s.step4F3}</strong></li>
                                <li><strong>{s.step4F4}</strong></li>
                                <li><strong>{s.step4F5}</strong></li>
                            </ul>
                        </div>

                        {/* Step 5 */}
                        <div style={styles.section}>
                            <h4 style={{ ...styles.sectionTitle, color: '#047857' }}>
                                <span style={{ ...styles.flowNum, backgroundColor: '#10b981', width: '28px', height: '28px', fontSize: '0.85rem' }}>5</span>
                                {s.step5Title}
                                <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#047857' }}>{s.badgeFinal}</span>
                            </h4>
                            <p style={styles.sectionText}>{s.step5Body}</p>
                            <div style={styles.mockField}>
                                <div style={{ padding: '10px', backgroundColor: '#ecfdf5', borderRadius: '8px', textAlign: 'center', fontSize: '0.85rem', fontFamily }}>
                                    {s.step5MockSaved}
                                    <div style={{ marginTop: '8px' }}>
                                        <span style={{ backgroundColor: '#3b82f6', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '0.8rem', fontFamily }}>{s.step5MockPrint}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Important Notes */}
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '12px', color: '#1e293b', marginTop: '8px', fontFamily }}>
                            {s.notesTitle}
                        </h3>
                        <div style={{ ...styles.callout, ...styles.calloutRed }}>
                            {s.note1}
                        </div>
                        <div style={{ ...styles.callout, ...styles.calloutAmber }}>
                            {s.note2}
                        </div>
                        <div style={{ ...styles.callout, ...styles.calloutAmber }}>
                            {s.note3}
                        </div>
                        <div style={{ ...styles.callout, ...styles.calloutGreen }}>
                            {s.note4}
                        </div>
                        <div style={{ ...styles.callout, ...styles.calloutBlue }}>
                            {s.note5}
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={styles.footer}>
                        {s.footer}
                    </div>
                </div>
            </div>
        </>
    );
}
